import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import PhoneMissedIcon from "@mui/icons-material/PhoneMissed";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  setFrajunPhoneNumber,
  setFrajunContactName,
  setFrajunContactId,
  setVisible,
  setCallCompleteContactId,
  setCallSoundEnabled,
  setCallNotificationEnabled,
} from "services/Redux/userToken";
import { useRingtone } from "hooks/useRingtone";
import {
  requestNotificationPermission,
  showCallNotification,
} from "utils/browserNotification";

const AUTO_DISMISS_MS = 60000;
const MISSED_STORAGE_KEY = "crm_missed_calls";

function loadMissedFromStorage() {
  try {
    return JSON.parse(sessionStorage.getItem(MISSED_STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveMissedToStorage(list) {
  try {
    sessionStorage.setItem(MISSED_STORAGE_KEY, JSON.stringify(list));
  } catch {}
}

export default function IncomingCallNotification() {
  const { domain, token, userDetails } = useSelector((s) => s.tokenReducer);
  const currentUserId = userDetails?.id ? String(userDetails.id) : null;
  const callSoundEnabled = useSelector((s) => s.tokenReducer.callSoundEnabled);
  const callNotificationEnabled = useSelector(
    (s) => s.tokenReducer.callNotificationEnabled
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const [notification, setNotification] = useState(null);
  const [missedCalls, setMissedCalls] = useState(loadMissedFromStorage);
  const socketRef = useRef(null);
  const dismissTimerRef = useRef(null);
  const intentionallyClosedRef = useRef(false);
  const reconnectTimerRef = useRef(null);
  const backoffRef = useRef(2000);
  const disconnectedSinceRef = useRef(null);
  const browserNotifRef = useRef(null);
  const lastProcessedCallRef = useRef({});
  const missedDismissTimersRef = useRef(new Map());

  const { play: playRing, stop: stopRing } = useRingtone();

  const stopAlerts = () => {
    stopRing();
    if (browserNotifRef.current) {
      browserNotifRef.current.close();
      browserNotifRef.current = null;
    }
  };

  useEffect(() => {
    if (!token) return;

    intentionallyClosedRef.current = false;
    backoffRef.current = 2000;
    disconnectedSinceRef.current = null;

    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const url = `${protocol}://${window.location.host}/ws/crm/notifications/?token=${token}`;

    function connect() {
      const ws = new WebSocket(url);
      socketRef.current = ws;

      ws.onopen = () => {
        backoffRef.current = 2000;
        disconnectedSinceRef.current = null;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "incoming_call_notification") {
            clearTimeout(dismissTimerRef.current);
            setNotification(data);
            dismissTimerRef.current = setTimeout(() => {
              stopAlerts();
              setNotification(null);
            }, AUTO_DISMISS_MS);

            if (callSoundEnabled) {
              playRing();
            }

            if (callNotificationEnabled) {
              requestNotificationPermission().then((granted) => {
                if (granted) {
                  const callerDisplay =
                    data.contact_name ||
                    data.company_name ||
                    data.phone_number ||
                    "Unknown caller";
                  const body =
                    data.company_name && data.contact_name
                      ? `${callerDisplay} · ${data.company_name}`
                      : callerDisplay;
                  browserNotifRef.current = showCallNotification({
                    title: "📞 Incoming call",
                    body,
                  });
                }
              });
            }
          }

          if (data.type === "call_claimed") {
            setNotification((prev) => {
              if (prev?.call_id === data.call_id) {
                clearTimeout(dismissTimerRef.current);
                stopAlerts();
                toast.info(`Call taken by ${data.claimed_by}`, {
                  autoClose: 3000,
                });
                return null;
              }
              return prev;
            });
          }

          if (data.type === "missed_call_notification") {
            // Defence-in-depth: the backend already scopes this broadcast to
            // the owning agent (or managers, when unassigned). Re-check on
            // the client and suppress silently on a mismatch rather than
            // trusting the channel routing alone.
            const ownerId =
              data.assigned_agent_id != null
                ? String(data.assigned_agent_id)
                : null;
            if (ownerId && currentUserId && ownerId !== currentUserId) {
              return;
            }

            // Global call_id dedup guard — catches backend double-fires of the
            // exact same call_id within a 5-second window.
            const nowMs = Date.now();
            const lastSeen = lastProcessedCallRef.current[data.call_id];
            if (lastSeen && nowMs - lastSeen < 5000) return;
            lastProcessedCallRef.current[data.call_id] = nowMs;

            stopAlerts();
            setMissedCalls((prev) => {
              const existingIdx = prev.findIndex(
                (m) => m.phone_number === data.phone_number
              );
              let updated;
              if (existingIdx !== -1) {
                // Increment count on the existing card rather than adding a second
                updated = prev.map((m, i) =>
                  i === existingIdx
                    ? { ...m, missedCount: (m.missedCount || 1) + 1 }
                    : m
                );
              } else {
                updated = [{ ...data, missedCount: 1 }, ...prev].slice(0, 5);
              }
              saveMissedToStorage(updated);
              return updated;
            });

            // Reset (or start) the auto-dismiss timer for this phone number so
            // a freshly-arrived duplicate doesn't disappear immediately.
            const phoneKey = data.phone_number;
            if (missedDismissTimersRef.current.has(phoneKey)) {
              clearTimeout(missedDismissTimersRef.current.get(phoneKey));
            }
            const timerId = setTimeout(() => {
              setMissedCalls((prev) => {
                const updated = prev.filter((m) => m.phone_number !== phoneKey);
                saveMissedToStorage(updated);
                return updated;
              });
              missedDismissTimersRef.current.delete(phoneKey);
            }, AUTO_DISMISS_MS);
            missedDismissTimersRef.current.set(phoneKey, timerId);
          }

          if (data.type === "activity_saved") {
            dispatch(setCallCompleteContactId(data.contact_id));
            setTimeout(() => dispatch(setCallCompleteContactId(null)), 2000);
          }
        } catch (e) {
          console.error("[IncomingCallNotification] WS parse error", e);
        }
      };

      ws.onerror = (err) =>
        console.error("[IncomingCallNotification] WS error", err);

      ws.onclose = () => {
        if (intentionallyClosedRef.current) return;
        if (!disconnectedSinceRef.current) {
          disconnectedSinceRef.current = Date.now();
        }
        const delay = backoffRef.current;
        backoffRef.current = Math.min(backoffRef.current * 2, 30000);
        if (
          disconnectedSinceRef.current &&
          Date.now() - disconnectedSinceRef.current > 60000 &&
          window.location.pathname.startsWith('/admin/crm')
        ) {
          toast.warn("Call notifications disconnected. Reconnecting…", {
            autoClose: 5000,
          });
        }
        reconnectTimerRef.current = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      intentionallyClosedRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      clearTimeout(dismissTimerRef.current);
      if (socketRef.current) socketRef.current.close();
      stopRing();
      missedDismissTimersRef.current.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAccept = async () => {
    if (!notification) return;
    stopAlerts();
    try {
      let res = await axios.post(
        `${domain}crm/acknowledge/`,
        { call_id: notification.call_id },
        { headers: { Authorization: `token ${token}` } }
      );
      if (res.status === 202) {
        await new Promise((r) => setTimeout(r, 2000));
        res = await axios.post(
          `${domain}crm/acknowledge/`,
          { call_id: notification.call_id },
          { headers: { Authorization: `token ${token}` } }
        );
      }
      if (res.status === 200) {
        clearTimeout(dismissTimerRef.current);
        setNotification(null);
        toast.success("Call accepted — you are now attending this call.");
      } else {
        toast.warning("Call is still being set up. Please try again.");
      }
    } catch (err) {
      console.error('[Incomingcallnotification] operation failed:', err);
      toast.error(err?.response?.data?.error || "Failed to acknowledge call.");
    }
  };

  const handleDismiss = () => {
    clearTimeout(dismissTimerRef.current);
    stopAlerts();
    setNotification(null);
  };

  const handleDismissMissed = (callId) => {
    setMissedCalls((prev) => {
      const target = prev.find((m) => m.call_id === callId);
      if (target) {
        const phoneKey = target.phone_number;
        if (missedDismissTimersRef.current.has(phoneKey)) {
          clearTimeout(missedDismissTimersRef.current.get(phoneKey));
          missedDismissTimersRef.current.delete(phoneKey);
        }
      }
      const updated = prev.filter((m) => m.call_id !== callId);
      saveMissedToStorage(updated);
      return updated;
    });
  };

  const handleCallBack = (missed) => {
    if (!missed.phone_number) return;
    dispatch(setFrajunPhoneNumber(missed.phone_number));
    if (missed.contact_name) {
      dispatch(setFrajunContactName(missed.contact_name));
    }
    if (missed.contact_id) {
      dispatch(setFrajunContactId(missed.contact_id));
    }
    dispatch(setVisible(true));
    handleDismissMissed(missed.call_id);
  };

  const handleToggleSound = () => {
    dispatch(setCallSoundEnabled(!callSoundEnabled));
  };

  const handleToggleNotification = () => {
    if (!callNotificationEnabled) {
      requestNotificationPermission();
    }
    dispatch(setCallNotificationEnabled(!callNotificationEnabled));
  };

  return (
    <>
      {/* Settings toggle pill — bottom-left, opposite the softphone widget */}
      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          left: 16,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          background: "white",
          border: "0.5px solid #E0E0E0",
          borderRadius: 20,
          padding: "4px 8px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
          zIndex: 1000,
        }}
      >
        <Tooltip
          title={
            callSoundEnabled
              ? "Ring sound ON — click to mute"
              : "Ring sound OFF — click to enable"
          }
        >
          <IconButton
            size="small"
            onClick={handleToggleSound}
            sx={{ color: callSoundEnabled ? "#1D9E75" : "#999" }}
          >
            {callSoundEnabled ? (
              <VolumeUpIcon sx={{ fontSize: 18 }} />
            ) : (
              <VolumeOffIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip
          title={
            callNotificationEnabled
              ? "Browser notifications ON — click to disable"
              : "Browser notifications OFF — click to enable"
          }
        >
          <IconButton
            size="small"
            onClick={handleToggleNotification}
            sx={{ color: callNotificationEnabled ? "#1D9E75" : "#999" }}
          >
            {callNotificationEnabled ? (
              <NotificationsActiveIcon sx={{ fontSize: 18 }} />
            ) : (
              <NotificationsOffIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Missed call banners */}
      {missedCalls.map((missed, idx) => (
        <Box
          key={missed.call_id || idx}
          sx={{
            position: "fixed",
            bottom: notification ? 24 + (idx + 1) * 180 : 24 + idx * 180,
            right: 24,
            zIndex: 1999,
            minWidth: 300,
            maxWidth: 360,
          }}
        >
          <Card
            elevation={6}
            sx={{ borderLeft: "4px solid #BA7517", borderRadius: 2 }}
          >
            <CardContent sx={{ pb: "12px !important" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhoneMissedIcon
                    sx={{ color: "#BA7517" }}
                    fontSize="small"
                  />
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ color: "#BA7517" }}
                  >
                    {missed.missedCount > 1
                      ? `${missed.missedCount} missed calls`
                      : "Missed call"}
                  </Typography>
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => handleDismissMissed(missed.call_id)}
                  sx={{ mt: -0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Box mt={1}>
                {missed.contact_name && (
                  <Typography variant="body1" fontWeight={700}>
                    {missed.contact_name}
                  </Typography>
                )}
                {missed.company_name && (
                  <Typography variant="body2" color="text.secondary">
                    {missed.company_name}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontFamily="monospace"
                >
                  {missed.phone_number}
                </Typography>
                {missed.assigned_agent && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 13, color: "text.disabled" }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Agent: {missed.assigned_agent}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box mt={1.5}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhoneIcon />}
                  sx={{ fontSize: 11, borderColor: "#BA7517", color: "#BA7517" }}
                  onClick={() => handleCallBack(missed)}
                >
                  Call back
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      ))}

      {/* Incoming call banner */}
      {notification && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 2000,
            minWidth: 320,
            maxWidth: 380,
          }}
        >
          <Card
            elevation={8}
            sx={{
              borderLeft: notification.escalated
                ? "4px solid #f44336"
                : "4px solid #2196f3",
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ pb: "12px !important" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhoneIcon
                    color={notification.escalated ? "error" : "primary"}
                    fontSize="small"
                  />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Incoming Call
                  </Typography>
                  {notification.escalated && (
                    <Chip label="Escalated" color="error" size="small" />
                  )}
                </Stack>
                <IconButton
                  size="small"
                  onClick={handleDismiss}
                  sx={{ mt: -0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Box mt={1}>
                {notification.contact_id ? (
                  <>
                    <Typography variant="body1" fontWeight={700}>
                      {notification.contact_name}
                    </Typography>
                    {notification.company_name && (
                      <Typography variant="body2" color="text.secondary">
                        {notification.company_name}
                      </Typography>
                    )}
                    {notification.assigned_agent && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        <PersonIcon
                          sx={{ fontSize: 13, color: "text.disabled" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Agent: {notification.assigned_agent}
                        </Typography>
                      </Box>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontFamily="monospace"
                    >
                      {notification.phone_number}
                    </Typography>
                    {notification.contact_status && (
                      <Typography variant="caption" color="text.secondary">
                        Status: {notification.contact_status}
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Unknown caller
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontFamily="monospace"
                    >
                      {notification.phone_number}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      sx={{ mt: 1, color: "teal", borderColor: "teal" }}
                      onClick={() =>
                        history.push(
                          `/admin/crm/create?phone=${encodeURIComponent(
                            notification.phone_number || ""
                          )}`
                        )
                      }
                    >
                      Add as contact
                    </Button>
                  </>
                )}
              </Box>

              {notification.escalated && notification.original_agent && (
                <Typography
                  variant="caption"
                  color="error"
                  display="block"
                  mt={0.5}
                >
                  {notification.original_agent} did not respond
                </Typography>
              )}

              <Stack direction="row" spacing={1} mt={2}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<PhoneIcon />}
                  onClick={handleAccept}
                  fullWidth
                >
                  Answer
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleDismiss}
                  fullWidth
                >
                  Decline
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}
    </>
  );
}
